import {
  Button,
  Field,
  mergeClasses,
  Spinner,
  Tooltip,
} from "@fluentui/react-components";
import { Clock16Regular } from "@fluentui/react-icons";
import { ConstantValues } from "common/constants";
import { AIModelSettings } from "components/aiModelSettings/AIModelSettings";
import { CustomDialog } from "components/customDialog/CustomDialog";
import { useResizer } from "hooks/useResizer";
import { LLMType } from "models/enums/LLMType";
import { FileWithId } from "models/FileWithId";
import { IConversation } from "models/IConversation";
import { IFile } from "models/IFile";
import { IMessage } from "models/IMessage";
import { IAIResponseError } from "models/IResponseError";
import { ITag } from "models/ITag";
import { IUser } from "models/IUser";
import { IWorkspace } from "models/IWorkspace";
import { useEffect, useRef, useState } from "react";
import {
  InfiniteData,
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { useSharedButtonStyles } from "theme/sharedStyles";
import { InvalidUploadFileError, uploadFilesToStorage } from "utils/blobUtils";
import { v4 as uuidv4 } from "uuid";
import {
  addMessageToConversation,
  addTemporaryChatUserUsage,
  createConversation,
  getAIResponse,
  getAITitle,
  getMessagesOfConversation,
  getPrompts,
  updateConversationTitle,
} from "../../../api";
import { getChunkAndUpdateAIMessage } from "../../../common/streamProcessing.js";
import {
  addValueToAIGenerateMessage,
  resetAIGeneratedMessageSlice,
  setIsGenerating,
} from "../../../redux/aiGeneratedMessage";
import { setCurrentConversation } from "../../../redux/conversations";
import { setCurrentFiles } from "../../../redux/files";
import { ILLMConfig, setNewConfigForKey } from "../../../redux/llmConfig";
import { addMessageToCurrentMessages } from "../../../redux/messages";
import { setCurrentPrompt } from "../../../redux/selectedPrompt";
import { setTagsForKey } from "../../../redux/tags";
import { setTemporaryChatOpen } from "../../../redux/temporaryChat";
import { setLibraryDocumentsForKey } from "../../../redux/workspaceLibraryDocuments";
import { AddPrompt } from "../addPrompt/AddPrompt";
import { FilePreview } from "../filePreview/FilePreview";
import { FileUploader } from "../fileUploader/FileUploader";
import { MessageInput } from "../messageInput/MessageInput";
import { useStyles } from "./MessageBar.styles";
import { IConversationsPage, IMessageBarProps } from "./MessageBar.types";

const updateConversationsCache = (
  queryClient: QueryClient,
  conversationId: string,
  user: { id: string; displayName: string },
): void => {
  const nowTimestamp = Math.floor(Date.now() / 1000);

  queryClient.setQueriesData<InfiniteData<IConversationsPage>>(
    ["conversations"],
    (oldData) => {
      if (!oldData?.pages) return oldData as InfiniteData<IConversationsPage>;

      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          conversations: page.conversations.map((c) =>
            c.id === conversationId
              ? {
                  ...c,
                  updatedAt: nowTimestamp,
                  lastUpdatedByDisplayName: user.displayName,
                  lastUpdatedByUserId: user.id,
                }
              : c,
          ),
        })),
      };
    },
  );
};

export const MessageBar = ({
  setIsMessageLoading,
  setAutoScrollEnabled,
  isEditMessageActive,
  setIsEditMessageActive,
  promptToEdit,
  setPromptToEdit,
}: IMessageBarProps): JSX.Element => {
  const queryClient = useQueryClient();
  const { data: prompts } = useQuery(
    "prompts",
    async () => {
      const prompts = await getPrompts();
      return prompts;
    },
    { refetchOnWindowFocus: false },
  );

  const [text, setText] = useState<string | null>(null);
  const [uploadFiles, setUploadFiles] = useState<FileWithId[]>([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [unsupportedFileMessage, setUnsupportedFileMessage] = useState<
    string | null
  >(null);
  const [incompleteMessage, setIncompleteMessage] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const abortControllerRef = useRef<AbortController | null>(null);
  const sharedButtonStyles = useSharedButtonStyles();
  const styles = useStyles();
  const dispatch = useDispatch();
  const isMobile = useResizer();

  const isTemporaryChatOpen = useSelector(
    (state: RootState) => state.temporaryChatSlice.isOpen,
  );

  const workspaces: IWorkspace[] = useSelector(
    (state: RootState) => state.workspacesSlice.value,
  );
  const referencedIds: string[] =
    useSelector(
      (state: RootState) =>
        state.referencedConversationsSlice.conversations.map(
          (conversation: IConversation) =>
            conversation.id ?? ConstantValues.EMPTY_STRING,
        ),
      shallowEqual,
    ) || [];
  const isCitationsLoading = useSelector(
    (state: RootState) => state.citationsSlice.isLoading,
  );
  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  ) as IUser | null;
  const currentlySelectedConversation: IConversation = useSelector(
    (state: RootState) => state.currentConversationSetter.value,
  );
  const currentMessages: IMessage[] = useSelector(
    (state: RootState) => state.currentMessagesSetter.value,
  );
  const currentLLMConfig = useSelector(
    (state: RootState) =>
      state.currentLLMConfigSlice.config[ConstantValues.CHAT] ?? {
        llmType: LLMType.CLAUDE,
        rag: false,
        citate: false,
      },
  );
  const currentPrompt = useSelector(
    (state: RootState) => state.currentPromptSlice.prompts[ConstantValues.CHAT],
  ) as { id: string; description: string } | null;
  const activeWorkspace = useSelector(
    (state: RootState) => state.activeWorkspaceSlice.value,
  );
  const conversationKey =
    currentlySelectedConversation?.id || ConstantValues.CHAT;
  const currentTags: ITag[] = useSelector(
    (state: RootState) => state.currentTagsSlice.tags[conversationKey] || [],
  );
  const workspaceLibraryDocuments = useSelector(
    (state: RootState) =>
      state.workspaceLibraryDocumentsSlice.documents[conversationKey] || [],
  );
  const referencedConversations: IConversation[] = useSelector(
    (state: RootState) => state.referencedConversationsSlice.conversations,
  );

  const addMessageToChat = async ({
    text,
    currentMessages,
    currentlySelectedConversation,
    uploadFiles,
    currentTags,
    currentLLMConfig,
    setIsMessageLoading,
    setText,
    setUploadFiles,
    dispatch,
    queryClient,
    abortControllerRef,
  }: {
    text: string | null;
    currentMessages: IMessage[];
    currentlySelectedConversation: IConversation | null;
    uploadFiles: FileWithId[];
    currentTags: ITag[];
    currentLLMConfig: ILLMConfig;
    setIsMessageLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setText: React.Dispatch<React.SetStateAction<string | null>>;
    setUploadFiles: React.Dispatch<React.SetStateAction<FileWithId[]>>;
    dispatch: ReturnType<typeof useDispatch>;
    queryClient: QueryClient;
    abortControllerRef: React.MutableRefObject<AbortController | null>;
  }) => {
    setIncompleteMessage(ConstantValues.EMPTY_STRING);

    if (!text || !text.trim()) {
      setText(ConstantValues.EMPTY_STRING);
      return;
    }

    abortControllerRef.current = new AbortController();

    setIsMessageLoading(true);
    dispatch(setIsGenerating(true));

    let createdConversation = { ...currentlySelectedConversation };
    if (!currentlySelectedConversation?.id) {
      const conversationPayload = {
        title: "New Chat",
        pinned: false,
        messages: [],
        workspaceId: activeWorkspace?.id ?? ConstantValues.EMPTY_STRING,
      };
      createdConversation = await createConversation({
        conversation: conversationPayload,
      });
      dispatch(setCurrentConversation(createdConversation));

      if (createdConversation.id) {
        if (workspaceLibraryDocuments.length > 0) {
          dispatch(
            setLibraryDocumentsForKey({
              key: createdConversation.id,
              documents: workspaceLibraryDocuments,
            }),
          );
        }
        if (currentTags.length > 0) {
          dispatch(
            setTagsForKey({
              key: createdConversation.id,
              tags: currentTags,
            }),
          );
        }
      }
    }

    const personalWorkspace = workspaces.find(
      (workspace: IWorkspace) =>
        workspace.name === ConstantValues.PERSONAL_WORKSPACE,
    );
    let entityId: string = currentUser?.id ?? ConstantValues.EMPTY_STRING;
    let prefix: "user_store" | "workspace" = "user_store";
    if (
      personalWorkspace &&
      activeWorkspace &&
      activeWorkspace.id !== personalWorkspace.id
    ) {
      entityId = activeWorkspace.id;
      prefix = "workspace";
    }

    let responseNames: IFile[];
    try {
      responseNames = await uploadFilesToStorage(
        uploadFiles,
        prefix,
        entityId ?? ConstantValues.EMPTY_STRING,
        false,
        createdConversation?.id,
      );
    } catch (e) {
      console.error("File upload failed:", e);
      throw new InvalidUploadFileError("");
    }
    const newMessage = await addMessageToConversation({
      message: {
        content: text,
        files: responseNames,
        isHidden: false,
        role: "user",
        tags: currentTags,
        llmConfig: currentLLMConfig,
        referencedWorkspaceConversationIds: referencedConversations.map(
          (c) => c.id,
        ),
        selectedLibraryDocuments: workspaceLibraryDocuments,
      },
      conversationId: createdConversation?.id,
    });

    setText(ConstantValues.EMPTY_STRING);
    setUploadFiles([]);
    dispatch(setCurrentFiles([]));
    dispatch(addMessageToCurrentMessages(newMessage));

    const referencedResults = await Promise.all(
      referencedIds.map((id) =>
        getMessagesOfConversation({ conversationId: id }).catch((err) => {
          console.error(
            "Failed to load referenced messages for conversation:",
            id,
            err,
          );
          return [] as IMessage[];
        }),
      ),
    );
    const referencedMessages = referencedResults.flat();
    const combinedMessages = [
      ...referencedMessages,
      ...currentMessages,
      newMessage,
    ];

    const libraryFiles =
      workspaceLibraryDocuments.length > 0
        ? workspaceLibraryDocuments.map((doc) => doc.filename)
        : null;
    const workspaceIdValue = activeWorkspace?.id ?? null;

    // Cast needed: api.js default params (`= null`, `= []`) cause TS to infer null/never[] types
    const response = await (
      getAIResponse as (...args: unknown[]) => Promise<unknown>
    )({
      llmConfig: currentLLMConfig,
      messages: [...combinedMessages, newMessage],
      abortSignal: abortControllerRef.current!.signal,
      workspaceLibraryFiles: libraryFiles ?? [],
      workspaceId: workspaceIdValue ?? ConstantValues.EMPTY_STRING,
    });

    if ("error" in response) {
      throw new Error(
        `AI Response Error: ${response.status || "Unknown"} - ${response.message || "Something went wrong"}`,
      );
    }
    const {
      content,
      chartData,
      sourceDocuments,
      citations,
      webSearchCitations,
      // Cast needed: streamProcessing.js default params `= null` cause TS to infer null types
    } = await (
      getChunkAndUpdateAIMessage as (...args: unknown[]) => Promise<any>
    )(
      response,
      response.body,
      dispatch,
      (value: string) => setIncompleteMessage(value),
      setIsMessageLoading,
      dispatch,
    );
    if (!content) {
      setIsMessageLoading(false);
      dispatch(setIsGenerating(false));
      return;
    }

    const newAIMessage = await addMessageToConversation({
      message: {
        ...(webSearchCitations?.length > 0 && { webSearchCitations }),
        citations,
        content,
        files: responseNames,
        isHidden: false,
        chartData,
        role: "assistant",
        tags: currentTags,
        sourceDocuments,
        llmConfig: currentLLMConfig,
        referencedWorkspaceConversationIds: referencedConversations.map(
          (c) => c.id,
        ),
        selectedLibraryDocuments: workspaceLibraryDocuments,
      },
      conversationId: createdConversation?.id,
    });

    dispatch(addMessageToCurrentMessages(newAIMessage));
    dispatch(resetAIGeneratedMessageSlice());
    setIsMessageLoading(false);
    dispatch(setIsGenerating(false));

    if (currentMessages?.length === 0) {
      const generatedTitle = await getAITitle({
        llmConfig: currentLLMConfig,
        messages: [...currentMessages, newMessage, newAIMessage],
      });
      if (!generatedTitle?.title) {
        queryClient.invalidateQueries({ queryKey: ["conversations"] });
        return;
      }

      const sanitizedTitle: string = generatedTitle.title
        .replace(/^"|"$/g, ConstantValues.EMPTY_STRING)
        .trim();
      if (sanitizedTitle === ConstantValues.EMPTY_STRING) {
        queryClient.invalidateQueries({ queryKey: ["conversations"] });
        return;
      }

      const updatedConversation = await updateConversationTitle({
        conversation: createdConversation,
        title: sanitizedTitle,
      });
      dispatch(setCurrentConversation(updatedConversation));
      localStorage.setItem(
        ConstantValues.DRAFT_MESSAGE_CONVERSATION_ID,
        updatedConversation.id,
      );
    }

    if (createdConversation?.id && currentUser) {
      updateConversationsCache(
        queryClient,
        createdConversation.id,
        currentUser,
      );
    }

    queryClient.invalidateQueries({ queryKey: ["conversations"] });
  };

  const chatMutation = useMutation({
    mutationFn: addMessageToChat,
    onError: async (error: IAIResponseError) => {
      if (abortControllerRef.current?.signal?.aborted) {
        setIsMessageLoading(false);
        dispatch(setIsGenerating(false));
        return;
      }

      let newAIMessage: Awaited<
        ReturnType<typeof addMessageToConversation>
      > | null;
      const errorMessage =
        error?.message || "Something went wrong. Please try again later.";

      let message = `**Error:** ${errorMessage}\n\nIf this issue persists, please [contact support](mailto:nhhelpu@northhighland.com?subject=AIMS%20Chat%20Error%20Received).`;
      if (error instanceof InvalidUploadFileError) {
        message = `**Critical Error:** Uploaded file is not in allowed list!`;
      }

      try {
        newAIMessage = await addMessageToConversation({
          message: {
            content: message,
            files: [],
            isHidden: false,
            role: "assistant",
            tags: currentTags,
            sourceDocuments: [],
            llmConfig: currentLLMConfig,
            selectedLibraryDocuments: workspaceLibraryDocuments,
          },
          conversationId: currentlySelectedConversation?.id,
        });
      } catch {
        let fallbackMessage = `**Critical Error:** ${errorMessage}\n\nIf this issue persists, please [contact support](mailto:nhhelpu@northhighland.com?subject=AIMS%20Chat%20Error%20Received).`;
        if (error instanceof InvalidUploadFileError) {
          fallbackMessage = `**Critical Error:** Uploaded file type is not in allowed list!`;
        }

        newAIMessage = {
          id: uuidv4(),
          content: fallbackMessage,
          files: [],
          isHidden: false,
          role: "assistant",
          tags: currentTags,
          sourceDocuments: [],
          llmConfig: currentLLMConfig,
          createdAt: Date.now(),
          selectedLibraryDocuments: workspaceLibraryDocuments,
        };
      } finally {
        if (newAIMessage) {
          dispatch(addMessageToCurrentMessages(newAIMessage));
          dispatch(resetAIGeneratedMessageSlice(newAIMessage));
        }
        setIsMessageLoading(false);
        dispatch(setIsGenerating(false));
      }
    },
  });

  const chatWithAi = () => {
    chatMutation.mutate({
      text,
      currentMessages,
      currentlySelectedConversation,
      uploadFiles,
      currentTags,
      currentLLMConfig,
      setIsMessageLoading,
      setText,
      setUploadFiles,
      dispatch,
      queryClient,
      abortControllerRef,
    });

    localStorage.removeItem(ConstantValues.DRAFT_MESSAGE);
    setAutoScrollEnabled(false);
    setIsEditMessageActive(false);
    setPromptToEdit(null);
  };

  const cancelFetch = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();

      dispatch(addValueToAIGenerateMessage("*Stopping response...*"));
      setIsMessageLoading(false);
      dispatch(setIsGenerating(false));

      const conversation = { ...currentlySelectedConversation };

      try {
        if (incompleteMessage !== ConstantValues.EMPTY_STRING) {
          const interruptedMessage = await addMessageToConversation({
            message: {
              id: uuidv4(),
              content: incompleteMessage || `*Response interrupted by user*`,
              files: [],
              isHidden: false,
              role: "assistant",
              tags: currentTags,
              sourceDocuments: [],
              llmConfig: currentLLMConfig,
              createdAt: Date.now(),
              citations: [],
              selectedLibraryDocuments: workspaceLibraryDocuments,
            },
            conversationId: conversation?.id,
          });

          dispatch(addMessageToCurrentMessages(interruptedMessage));
        }

        const newAIMessage = await addMessageToConversation({
          message: {
            id: uuidv4(),
            content: `*Response stopped by user*`,
            files: [],
            isHidden: false,
            role: "assistant",
            tags: [],
            sourceDocuments: [],
            llmConfig: currentLLMConfig,
            createdAt: Date.now(),
            citations: [],
            selectedLibraryDocuments: [],
          },
          conversationId: conversation?.id,
        });

        dispatch(addMessageToCurrentMessages(newAIMessage));
      } catch (e) {
        console.error("Failed to save interrupted message:", e);
        const errorAIMessage = {
          id: uuidv4(),
          content: `**Error:** Failed to save the interrupted response. Your message may not have been saved.\n\nIf this issue persists, please [contact support](mailto:nhhelpu@northhighland.com?subject=AIMS%20Chat%20Error%20Received).`,
          files: [],
          isHidden: false,
          role: "assistant",
          tags: currentTags,
          sourceDocuments: [],
          llmConfig: currentLLMConfig,
          createdAt: Date.now(),
          citations: [],
          selectedLibraryDocuments: workspaceLibraryDocuments,
        };
        dispatch(addMessageToCurrentMessages(errorAIMessage));
      } finally {
        dispatch(resetAIGeneratedMessageSlice());
        dispatch(setCurrentConversation(conversation));
        queryClient.invalidateQueries({ queryKey: ["conversations"] });
      }
    }
  };

  useEffect((): void => {
    if (isEditMessageActive && promptToEdit) {
      setText(promptToEdit);
    }
  }, [isEditMessageActive, promptToEdit]);

  useEffect((): void => {
    const persistedText = localStorage.getItem(ConstantValues.DRAFT_MESSAGE);

    if (persistedText && persistedText !== ConstantValues.EMPTY_STRING) {
      setText(persistedText);
    } else {
      setText(ConstantValues.EMPTY_STRING);
    }
    dispatch(setCurrentFiles([]));
    setUploadFiles([]);
  }, [currentlySelectedConversation?.id]);

  useEffect((): void => {
    setText(currentPrompt?.description ?? ConstantValues.EMPTY_STRING);
  }, [currentPrompt?.id, currentPrompt?.description]);

  const openImagePreview = (fileUrl: string) => () => {
    setPreviewImage(fileUrl);
  };

  const closeImagePreview = () => {
    setPreviewImage(null);
  };

  const handleFileSelect = (files: FileWithId[]) => {
    setUploadFiles((prev) => [...prev, ...files]);
    dispatch(setCurrentFiles(files.map((file) => file.file.name)));
  };

  const onEnterPress = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (chatMutation.isLoading) return;
      chatWithAi();
    }
  };

  const deselectFile = (fileWithId: FileWithId) => {
    const remainingFiles: FileWithId[] = uploadFiles.filter(
      (f: FileWithId): boolean => f.file.name !== fileWithId.file.name,
    );
    setUploadFiles(remainingFiles);
    dispatch(
      setCurrentFiles(remainingFiles.map((f: FileWithId) => f.file.name)),
    );
  };

  const handleTemporaryChatClick = (): void => {
    if (!isTemporaryChatOpen) {
      addTemporaryChatUserUsage();
    }

    dispatch(setTemporaryChatOpen(!isTemporaryChatOpen));
    dispatch(setTagsForKey({ key: ConstantValues.TEMPORARY_CHAT, tags: [] }));
    dispatch(
      setCurrentPrompt({ key: ConstantValues.TEMPORARY_CHAT, prompt: null }),
    );
    dispatch(
      setNewConfigForKey({
        key: ConstantValues.TEMPORARY_CHAT,
        config: { llmType: LLMType.CLAUDE, rag: false, citate: false },
      }),
    );
  };

  const canInteractWithConversation = (): boolean => {
    if (activeWorkspace?.archived) {
      return false;
    }
    return true;
  };

  const canInteract =
    currentlySelectedConversation == null || canInteractWithConversation();

  return (
    <div className={styles.mainWrapper}>
      {previewImage && (
        <CustomDialog isOpen onClose={closeImagePreview} title="Image Preview">
          <img
            src={previewImage}
            alt="Uploaded file preview"
            className={styles.modalImage}
          />
        </CustomDialog>
      )}

      {isCitationsLoading && (
        <div className={styles.citationsLoading}>
          <Field
            validationMessage="Processing Citations"
            validationState="none"
          >
            <Spinner />
          </Field>
        </div>
      )}

      <div
        className={isMobile ? styles.mainContainerMobile : styles.mainContainer}
      >
        <FilePreview
          files={uploadFiles}
          onRemove={deselectFile}
          onPreview={openImagePreview}
        />

        <div
          className={
            isMobile ? styles.buttonsContainerMobile : styles.buttonsContainer
          }
        >
          {canInteract ? (
            <AIModelSettings pageName={ConstantValues.CHAT} />
          ) : null}
        </div>
        {canInteract ? (
          <div
            className={
              isMobile ? styles.fieldContainerMobile : styles.fieldContainer
            }
          >
            <CustomDialog
              isOpen={!!unsupportedFileMessage}
              onClose={() => setUnsupportedFileMessage(null)}
              title="Unsupported File Type"
            >
              <p>{unsupportedFileMessage}</p>
            </CustomDialog>

            <div
              className={
                isMobile ? styles.inputContainerMobile : styles.inputContainer
              }
            >
              <FileUploader
                onFileSelect={handleFileSelect}
                setUnsupportedFileMessage={setUnsupportedFileMessage}
              />

              <AddPrompt
                dictKey={ConstantValues.CHAT}
                canManagePrompt={true}
                data={prompts}
              />

              <Tooltip content={"Temporary Chat"} relationship={"label"}>
                <Button
                  aria-label="Temporary Chat"
                  className={mergeClasses(
                    sharedButtonStyles.ghostButton,
                    styles.temporaryChatButton,
                    isTemporaryChatOpen && styles.temporaryChatButtonActive,
                  )}
                  onClick={handleTemporaryChatClick}
                  icon={<Clock16Regular />}
                />
              </Tooltip>
              <MessageInput
                id={ConstantValues.CHAT}
                setUnsupportedFileMessage={setUnsupportedFileMessage}
                text={text}
                setText={setText}
                onSend={chatWithAi}
                onAbort={cancelFetch}
                onKeyPress={onEnterPress}
                disabled={chatMutation.isLoading}
                setUploadFiles={setUploadFiles}
              />
            </div>

            <div className={styles.helperText}>
              Enter to send, Shift+Enter for new line
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
